import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { StatCard } from '@/components/ui/StatCard';
import { StatusBadge, StatusIcon } from '@/components/ui/StatusBadge';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Sparkline } from '@/components/ui/Sparkline';
import { EventsWidget } from '@/components/EventsWidget';
import {
  Cpu,
  MemoryStick,
  HardDrive,
  Activity,
  Server,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { formatBytes, formatPercent, formatTimeAgo, formatUptime } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { cn } from '@/lib/utils';

const REFETCH = 5_000;

type Win = '15m' | '1h' | '6h' | '24h';
const WINDOWS: { value: Win; label: string }[] = [
  { value: '15m', label: '15m' },
  { value: '1h', label: '1h' },
  { value: '6h', label: '6h' },
  { value: '24h', label: '24h' },
];

export function OverviewRoute() {
  const now = useNow(1_000);
  const [window, setWindow] = useState<Win>('1h');
  const nodeQ = useQuery({
    queryKey: ['proxmox', 'node'],
    queryFn: endpoints.proxmox.node,
    refetchInterval: REFETCH,
  });
  const lxcQ = useQuery({
    queryKey: ['proxmox', 'lxc'],
    queryFn: endpoints.proxmox.lxc,
    refetchInterval: REFETCH,
  });
  const metricsQ = useQuery({
    queryKey: ['proxmox', 'metrics', window],
    queryFn: () => endpoints.proxmox.metrics(window),
    refetchInterval: REFETCH,
  });
  const servicesQ = useQuery({
    queryKey: ['services'],
    queryFn: endpoints.services.list,
    refetchInterval: REFETCH,
  });

  const node = nodeQ.data;
  const containers = lxcQ.data?.containers ?? [];
  const services = servicesQ.data?.services ?? [];
  const points = metricsQ.data?.points ?? [];

  const runningContainers = containers.filter((c) => c.status === 'running').length;
  const totalContainers = containers.length;
  const unhealthyServices = services.filter((s) => s.health.status === 'unhealthy');
  const unknownServices = services.filter((s) => s.health.status === 'unknown');
  const healthyServices = services.filter((s) => s.health.status === 'healthy');

  // Sparkline data: scale slice by window size
  const slice = window === '15m' ? -30 : window === '1h' ? -60 : window === '6h' ? -90 : -120;
  void slice; // suppress unused; slice currently unused since we plot all returned points
  const recentPoints = points.slice(slice);
  const cpuSeries = recentPoints.map((p) => p.cpu);
  const ramSeries = recentPoints.map((p) => p.ram_total > 0 ? (p.ram_used / p.ram_total) * 100 : 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Homelab at a glance · auto-refresh every {REFETCH / 1000}s
          </p>
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" />
          {servicesQ.data?.lastUpdated
            ? `polled ${formatTimeAgo(servicesQ.data.lastUpdated, now)}`
            : 'waiting for first poll…'}
        </div>
      </div>

      {/* Top stat row: host CPU/RAM/load/uptime */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Host CPU"
          value={
            node?.cpu ? (
              <span className="flex items-baseline gap-1">
                {node.cpu.percent.toFixed(1)}
                <span className="text-sm font-normal text-muted-foreground">%</span>
              </span>
            ) : (
              '—'
            )
          }
          icon={Cpu}
          hint={node?.cpu ? `load ${node.cpu.loadAverage.toFixed(2)}` : 'no data'}
          tone={
            !node?.cpu
              ? 'default'
              : node.cpu.percent < 60
                ? 'success'
                : node.cpu.percent < 85
                  ? 'warn'
                  : 'danger'
          }
        />
        <StatCard
          label="Host RAM"
          value={
            node?.memory ? (
              <span className="flex items-baseline gap-1">
                {formatPercent(node.memory.percent)}
                <span className="text-sm font-normal text-muted-foreground">
                  ({formatBytes(node.memory.used)})
                </span>
              </span>
            ) : (
              '—'
            )
          }
          icon={MemoryStick}
          hint={node?.memory ? `of ${formatBytes(node.memory.total)}` : 'no data'}
        />
        <StatCard
          label="Root FS"
          value={node?.rootfs ? formatPercent(node.rootfs.percent) : '—'}
          icon={HardDrive}
          hint={node?.rootfs ? formatBytes(node.rootfs.used) + ' used' : 'no data'}
        />
        <StatCard
          label="Uptime"
          value={node?.uptime ? formatUptime(node.uptime) : '—'}
          icon={Activity}
          hint={node?.pveVersion ? `PVE ${node.pveVersion.split('-')[0]}` : undefined}
        />
      </div>

      {/* Sparkline row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <CardHeader>
            <CardTitle>
              CPU · <span className="font-mono">{window}</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground tabular-nums">
                {cpuSeries.length > 0 ? `${cpuSeries[cpuSeries.length - 1]?.toFixed(1)}%` : '—'}
              </span>
              <div className="flex gap-0.5">
                {WINDOWS.map((w) => (
                  <button
                    key={w.value}
                    onClick={() => setWindow(w.value)}
                    className={cn(
                      'px-1.5 py-0.5 rounded text-[10px] font-mono',
                      window === w.value
                        ? 'bg-accent text-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
                    )}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardBody className="flex items-center justify-center min-h-[64px]">
            <Sparkline
              values={cpuSeries}
              width={420}
              height={48}
              tone={
                cpuSeries.length > 0 && cpuSeries[cpuSeries.length - 1] !== undefined
                  ? cpuSeries[cpuSeries.length - 1]! < 60
                    ? 'auto'
                    : cpuSeries[cpuSeries.length - 1]! < 85
                      ? 'warn'
                      : 'danger'
                  : 'auto'
              }
            />
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>
              RAM · <span className="font-mono">{window}</span>
            </CardTitle>
            <span className="text-xs text-muted-foreground tabular-nums">
              {ramSeries.length > 0 ? `${ramSeries[ramSeries.length - 1]?.toFixed(1)}%` : '—'}
            </span>
          </CardHeader>
          <CardBody className="flex items-center justify-center min-h-[64px]">
            <Sparkline
              values={ramSeries}
              width={420}
              height={48}
              domain={[0, 100]}
            />
          </CardBody>
        </Card>
      </div>

      {/* Service health summary */}
      <Card>
        <CardHeader>
          <CardTitle>Service health</CardTitle>
          <Link href="/services">
            <a className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
              all services <ArrowRight className="h-3 w-3" />
            </a>
          </Link>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <span className="text-2xl font-semibold tabular-nums">{healthyServices.length}</span>
              <span className="text-sm text-muted-foreground">healthy</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-rose-500" />
              <span className="text-2xl font-semibold tabular-nums">{unhealthyServices.length}</span>
              <span className="text-sm text-muted-foreground">down</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-zinc-500" />
              <span className="text-2xl font-semibold tabular-nums">{unknownServices.length}</span>
              <span className="text-sm text-muted-foreground">unknown</span>
            </div>
          </div>
          {unhealthyServices.length > 0 && (
            <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-sm">
              <div className="font-medium text-rose-400 mb-1">Down</div>
              <ul className="space-y-1">
                {unhealthyServices.map((s) => (
                  <li key={s.id} className="flex items-center gap-2 text-rose-300">
                    <StatusIcon status="unhealthy" />
                    <span className="font-medium">{s.displayName}</span>
                    {s.health.lastError && (
                      <span className="text-xs text-rose-400/70">— {s.health.lastError}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Containers + VMs at-a-glance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card>
          <CardHeader>
            <CardTitle>Containers</CardTitle>
            <span className="text-xs text-muted-foreground">
              {runningContainers} / {totalContainers} running
            </span>
          </CardHeader>
          <CardBody>
            <ProgressBar value={totalContainers === 0 ? 0 : (runningContainers / totalContainers) * 100} tone="success" />
            <div className="mt-3 text-xs text-muted-foreground flex items-center gap-2">
              <Server className="h-3.5 w-3.5" />
              <span>see full list on </span>
              <Link href="/proxmox" className="text-foreground hover:underline">
                Proxmox
              </Link>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>PVE status</CardTitle>
            {node ? (
              <StatusBadge status={node.online ? 'online' : 'offline'} />
            ) : (
              <StatusBadge status="unknown" />
            )}
          </CardHeader>
          <CardBody>
            <div className="space-y-1.5 text-sm">
              <Row label="Kernel" value={node?.kernel ?? '—'} />
              <Row label="CPU" value={node?.cpu ? `${node.cpu.model} · ${node.cpu.cores} cores` : '—'} />
              <Row
                label="Last poll"
                value={node?.lastUpdated ? formatTimeAgo(node.lastUpdated, now) : '—'}
              />
              {node?.error && (
                <div className="text-xs text-rose-400 mt-2 font-mono">{node.error}</div>
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Recent events */}
      <EventsWidget limit={20} />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-xs truncate text-right">{value}</span>
    </div>
  );
}
